import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import client, { apiErr } from "../lib/api";
import { Button, Input, Modal, Select } from "../components/ui";
import BrandFooter, { useBranding } from "../components/BrandFooter";
import { SiteMapDropdown } from "./Marketing";
import { translate as t, tp, BRAND, BRAND_TAGLINE, LANGS } from "../i18n";
import { Languages } from "lucide-react";

function ForgotModal({ open, onClose }) {
  const { lang } = useApp();
  const [step, setStep] = useState("request"); // request | otp | done
  const [email, setEmail] = useState("");
  const [method, setMethod] = useState("sms");
  const [otp, setOtp] = useState("");
  const [pw, setPw] = useState("");
  const [info, setInfo] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const request = async () => {
    setErr(""); setBusy(true);
    try {
      const { data } = await client.post("/auth/recover/request", { email: email.trim(), method, origin_url: window.location.origin });
      if (method === "sms") { setStep("otp"); setInfo(data.target ? `A 6-digit code was sent to ${data.target}.` : "If an account exists, a code has been sent to the phone on record."); }
      else { setStep("done"); setInfo(data.target ? `A recovery link was sent to ${data.target}.` : tp("If a recovery email is on record, a reset link has been sent.", lang)); }
    } catch (e) { setErr(apiErr(e)); }
    setBusy(false);
  };
  const verify = async () => {
    setErr("");
    if (pw.length < 6) return setErr(tp("Password must be at least 6 characters.", lang));
    setBusy(true);
    try {
      await client.post("/auth/recover/verify-otp", { email: email.trim(), otp: otp.trim(), new_password: pw });
      setStep("done"); setInfo("Your password has been reset. You can now sign in.");
    } catch (e) { setErr(apiErr(e)); }
    setBusy(false);
  };

  return (
    <Modal open={open} onClose={onClose} title="Recover your password" testid="forgot-modal">
      <div className="space-y-3 text-sm">
        {step === "request" && (<>
          <p className="opacity-70">Choose how you'd like to recover access. SMS sends a code to your phone of record; Email sends a secure link to your registered recovery email.</p>
          <Input label="Your login email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" />
          <Select label="Recovery method" value={method} onChange={(e) => setMethod(e.target.value)} data-testid="forgot-method">
            <option value="sms">SMS code to my phone</option>
            <option value="email">Link to my recovery email</option>
          </Select>
          <div className="flex justify-end gap-2"><Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={request} disabled={busy || !email} data-testid="forgot-request">Send recovery</Button></div>
        </>)}
        {step === "otp" && (<>
          {info && <p className="opacity-70">{info}</p>}
          <Input label="6-digit code" value={otp} onChange={(e) => setOtp(e.target.value)} data-testid="forgot-otp" />
          <Input label="New password" type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="forgot-newpw" />
          <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setStep("request")}>Back</Button>
            <Button onClick={verify} disabled={busy || !otp || !pw} data-testid="forgot-verify">Reset password</Button></div>
        </>)}
        {step === "done" && (<>
          <p className="text-[#24A148]">{info}</p>
          <div className="flex justify-end"><Button onClick={onClose} data-testid="forgot-done">Close</Button></div>
        </>)}
        {err && <p className="text-sm text-[#DA1E28]" data-testid="forgot-error">{err}</p>}
      </div>
    </Modal>
  );
}

export default function Login() {
  const { login, lang, chooseLang } = useApp();
  const nav = useNavigate();
  const [mode, setMode] = useState("login"); // login | register
  const [adminMode, setAdminMode] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", username: "", password: "" });
  const [channel, setChannel] = useState(localStorage.getItem("suite_mfa_channel") || "email");
  const [remember, setRemember] = useState(false);
  const [mfa, setMfa] = useState(null); // {challenge_id, channel, target, debug_otp?}
  const [code, setCode] = useState("");
  const [info, setInfo] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [scan, setScan] = useState(null);
  React.useEffect(() => {
    client.post("/oloso/scan", { url: window.location.href }).then((r) => setScan(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (scan?.blocked) { setErr(scan.disclosure); return; }
    setErr(""); setInfo(""); setBusy(true);
    try {
      if (mode === "register") {
        const { data } = await client.post("/auth/register", { name: form.name, email: form.email, password: form.password });
        login(data.token, data.user); nav("/app"); return;
      }
      const payload = adminMode
        ? { username: form.username, password: form.password, channel }
        : { email: form.email, password: form.password, channel };
      const dev = localStorage.getItem("suite_device_token");
      if (dev) payload.device_token = dev;
      localStorage.setItem("suite_mfa_channel", channel);
      const { data } = await client.post("/auth/login", payload);
      if (data.mfa_required) {
        setMfa(data);
        setInfo(`We sent a 6-digit code to ${data.target} (${data.channel === "sms" ? "SMS" : "email"}).`);
      } else { login(data.token, data.user); nav("/app"); }
    } catch (e2) { setErr(apiErr(e2)); } finally { setBusy(false); }
  };

  const verifyMfa = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await client.post("/auth/mfa/verify", { challenge_id: mfa.challenge_id, code: code.trim(), remember_device: remember, known_device: localStorage.getItem("suite_known_device") || undefined });
      if (data.device_token) localStorage.setItem("suite_device_token", data.device_token);
      if (data.known_device) localStorage.setItem("suite_known_device", data.known_device);
      login(data.token, data.user); nav("/app");
    } catch (e2) { setErr(apiErr(e2)); } finally { setBusy(false); }
  };

  const resendMfa = async () => {
    setErr(""); setInfo("");
    try {
      const { data } = await client.post("/auth/mfa/resend", { challenge_id: mfa.challenge_id });
      setMfa(data); setCode("");
      setInfo(`A new code was sent to ${data.target} (${data.channel === "sms" ? "SMS" : "email"}).`);
    } catch (e2) { setErr(apiErr(e2)); }
  };

  const resetToStart = () => { setMfa(null); setCode(""); setErr(""); setInfo(""); };
  const branding = useBranding();
  const suiteName = branding?.suite_name || BRAND;
  const tagline = branding?.suite_tagline || BRAND_TAGLINE;
  const privacyUrl = branding?.privacy_policy_url;
  const privacyMap = branding?.privacy_policy_content;
  const privacyText = (privacyMap && (privacyMap[lang] || privacyMap.ENG)) || "";
  const termsMap = branding?.terms_content;
  const termsText = (termsMap && (termsMap[lang] || termsMap.ENG)) || "";
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="absolute right-3 top-3 z-30 flex items-center gap-1.5 rounded-full border border-black/15 bg-white/90 px-2.5 py-1 text-xs shadow-sm backdrop-blur dark:border-white/20 dark:bg-black/60" data-testid="login-language-picker" title={tp("Change language", lang)}>
        <Languages size={14} className="shrink-0 opacity-70" />
        <select value={lang} onChange={(e) => chooseLang(e.target.value)} data-testid="login-language-select"
          dir="ltr" className="cursor-pointer bg-transparent font-medium text-black outline-none dark:text-white">
          {LANGS.map((l) => <option key={l.code} value={l.code} className="text-black">{l.native}</option>)}
        </select>
      </div>
      <div className="relative hidden lg:block">
        <img alt="enterprise" className="h-full w-full object-cover"
          src="https://images.unsplash.com/photo-1558196876-76dcb9d1993f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/10" />
        <div className="absolute bottom-12 left-12 max-w-none text-white">
          <img src="/brand-logo.png" alt={suiteName} className="mb-4 h-16 w-16 object-contain brightness-0 invert" />
          <h1 className="font-head text-[4.05rem] font-light leading-[1.05]" data-testid="landing-suite-name">{suiteName}</h1>
          <p className="mt-1 text-lg opacity-80" data-testid="landing-tagline">{tp(tagline, lang)}</p>
          <p className="mt-3 whitespace-nowrap text-sm opacity-80">
            {tp("· Familigi™ · BoxAlong™ · Currencida™ · SEBAA™, unified by the General Administration & Master Enabler.", lang)}
          </p>
        </div>
      </div>
      <div className="flex flex-col items-center justify-center p-8">
        <h1 className="mb-6 text-center font-head text-lg lg:hidden" data-testid="landing-suite-name-mobile">{suiteName}</h1>
        {scan && (
          <div data-testid="oloso-scan-notice" className={`mb-4 w-full max-w-sm rounded-lg border px-3 py-2 text-xs ${scan.blocked ? "border-[#DA1E28] bg-[#DA1E28]/10 text-[#DA1E28]" : "border-emerald-500/40 bg-emerald-500/5 text-emerald-700 dark:text-emerald-400"}`}>
            {tp("Your connection was security-scanned by the Onboard Local Operation Security Office (OLOSO).", lang)}{" "}
            {scan.blocked
              ? tp("Connection rejected: a security risk was detected on your device/network. Please contact the administrator.", lang)
              : tp("No threats detected — connection permitted.", lang)}
            {scan.blocked && <div className="mt-1 opacity-80">{tp("This can happen with automated tools or repeated attempts. Please wait ~2 minutes and refresh, or contact the administrator.", lang)}</div>}
          </div>
        )}
        {mfa ? (
          <form onSubmit={verifyMfa} className="w-full max-w-sm space-y-4" data-testid="mfa-form">
            <img src="/brand-logo.png" alt="Full Option Family Office" className="mb-2 h-12 w-12 object-contain dark:brightness-0 dark:invert" />
            <h2 className="font-head text-3xl">{tp("Verify it's you", lang)}</h2>
            {info && <p className="text-sm opacity-70" data-testid="mfa-info">{info}</p>}
            {mfa.debug_otp && <p className="text-xs text-[var(--primary)]" data-testid="mfa-debug-otp">Dev code: {mfa.debug_otp}</p>}
            <Input label={tp("6-digit code", lang)} value={code} inputMode="numeric" maxLength={6}
              onChange={(e) => setCode(e.target.value)} data-testid="mfa-code" />
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="mfa-remember-label">
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="mfa-remember" />
              {tp("Trust this device for 30 days (skip codes here)", lang)}
            </label>
            {err && <p className="text-sm text-[#DA1E28]" data-testid="mfa-error">{err}</p>}
            <Button type="submit" disabled={busy || code.length < 6} className="w-full" data-testid="mfa-verify">
              {busy ? "..." : tp("Verify & sign in", lang)}
            </Button>
            <div className="flex items-center justify-between text-sm">
              <button type="button" onClick={resendMfa} data-testid="mfa-resend" className="text-[var(--primary)] hover:underline">{tp("Resend code", lang)}</button>
              <button type="button" onClick={resetToStart} data-testid="mfa-back" className="opacity-70 hover:opacity-100">{tp("Back to sign in", lang)}</button>
            </div>
          </form>
        ) : (
        <form onSubmit={submit} className="w-full max-w-sm space-y-4" data-testid="auth-form">
          <img src="/brand-logo.png" alt="Full Option Family Office" className="mb-2 h-12 w-12 object-contain dark:brightness-0 dark:invert" />
          <h2 className="font-head text-3xl" data-testid="auth-title">
            {mode === "register" ? t("register", lang) : adminMode ? tp("Administrator sign-in", lang) : t("login", lang)}
          </h2>
          {mode === "register" && (
            <Input label={tp("Name", lang)} value={form.name} data-testid="name-input"
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
          )}
          {mode === "login" && adminMode ? (
            <Input label={tp("Username", lang)} required value={form.username} data-testid="username-input"
              onChange={(e) => setForm({ ...form, username: e.target.value })} />
          ) : (
            <Input label={tp("Email", lang)} type="email" required value={form.email} data-testid="email-input"
              onChange={(e) => setForm({ ...form, email: e.target.value })} />
          )}
          <Input label={tp("Password", lang)} type="password" required value={form.password} data-testid="password-input"
            onChange={(e) => setForm({ ...form, password: e.target.value })} />
          {mode === "login" && (
            <Select label={tp("Verification method (MFA)", lang)} value={channel} onChange={(e) => setChannel(e.target.value)} data-testid="mfa-channel">
              <option value="email">{tp("Email me a code", lang)}</option>
              <option value="sms">{tp("Text me a code (SMS)", lang)}</option>
            </Select>
          )}
          {err && <p className="text-sm text-[#DA1E28]" data-testid="auth-error">{err}</p>}
          <Button type="submit" disabled={busy} className="w-full" data-testid="auth-submit">
            {busy ? "..." : mode === "login" ? t("login", lang) : t("register", lang)}
          </Button>
          {!adminMode && (
            <button type="button" data-testid="toggle-auth-mode"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setErr(""); }}
              className="w-full text-sm opacity-70 hover:opacity-100">
              {mode === "login" ? tp("Need an account? Create one", lang) : tp("Have an account? Sign in", lang)}
            </button>
          )}
          {mode === "login" && (
            <button type="button" data-testid="forgot-password-link" onClick={() => setForgot(true)}
              className="w-full text-sm text-[var(--primary)] hover:underline">{tp("Forgot password?", lang)}</button>
          )}
          <button type="button" data-testid="admin-signin-toggle"
            onClick={() => { setAdminMode(!adminMode); setMode("login"); setErr(""); }}
            className="block w-full text-center text-xs font-medium text-[var(--primary)] hover:underline">
            {adminMode ? tp("← Back to standard sign-in", lang) : tp("Administrator sign-in →", lang)}
          </button>
          <a href="/support-login" data-testid="support-login-link"
            className="block w-full text-center text-xs opacity-50 hover:underline">{tp("Technical support staff sign-in →", lang)}</a>
          <a href="/demo-login" data-testid="demo-login-link"
            className="block w-full text-center text-xs font-medium text-[var(--primary)] hover:underline">{tp("Explore a live demo (no sign-up) →", lang)}</a>
          <a href="/claim-code" data-testid="claim-code-link"
            className="block w-full text-center text-xs opacity-50 hover:underline">{tp("Paid already? Get your activation code →", lang)}</a>
          <div className="flex justify-center pt-1" data-testid="login-sitemap-wrap">
            <SiteMapDropdown testid="login-sitemap" />
          </div>
          {privacyText ? (
            <div className="pt-1" data-testid="privacy-policy-section">
              <button type="button" onClick={() => setShowPrivacy((v) => !v)} data-testid="privacy-policy-toggle"
                className="block w-full text-center text-xs opacity-50 hover:underline">
                {tp("Privacy Policy", lang)} {showPrivacy ? "▲" : "▼"}
              </button>
              {showPrivacy && (
                <div data-testid="privacy-policy-content"
                  className="mt-2 max-h-56 overflow-auto whitespace-pre-wrap rounded-md border border-black/10 dark:border-white/10 bg-black/[0.03] dark:bg-white/[0.04] p-3 text-start text-xs opacity-80">
                  {privacyText}
                  {privacyUrl && (
                    <a href={privacyUrl} target="_blank" rel="noreferrer" data-testid="privacy-policy-link"
                      className="mt-2 block font-medium text-[var(--primary)] hover:underline">{tp("View full policy →", lang)}</a>
                  )}
                </div>
              )}
            </div>
          ) : privacyUrl && (
            <a href={privacyUrl} target="_blank" rel="noreferrer" data-testid="privacy-policy-link"
              className="block w-full pt-1 text-center text-xs opacity-50 hover:underline">{tp("Privacy Policy", lang)}</a>
          )}
          {termsText && (
            <div data-testid="terms-section">
              <button type="button" onClick={() => setShowTerms((v) => !v)} data-testid="terms-toggle"
                className="block w-full text-center text-xs opacity-50 hover:underline">
                {tp("Terms of Service", lang)} {showTerms ? "▲" : "▼"}
              </button>
              {showTerms && (
                <div data-testid="terms-content"
                  className="mt-2 max-h-56 overflow-auto whitespace-pre-wrap rounded-md border border-black/10 dark:border-white/10 bg-black/[0.03] dark:bg-white/[0.04] p-3 text-start text-xs opacity-80">
                  {termsText}
                </div>
              )}
            </div>
          )}
        </form>
        )}
      </div>
      <ForgotModal open={forgot} onClose={() => setForgot(false)} />
      <BrandFooter />
    </div>
  );
}
